import { Injectable } from '@angular/core';
import olMap from 'ol/Map';
import type olMapBrowserEvent from 'ol/MapBrowserEvent';

type DispatchFn = (e: any) => any;
interface State {
  originalDispatch: DispatchFn;
  blockedTypes: Set<string>;
  handlers: Partial<Record<string, (evt: olMapBrowserEvent<any>) => void>>;
}

interface PatchedOlMap extends olMap {
  __exclusiveMapEvents__?: State;
}

@Injectable({ providedIn: 'root' })
export class ExclusiveMapEventsService {
  enable(
    olMap: olMap,
    options: {
      block: string[];
      handlers?: Partial<Record<string, (evt: olMapBrowserEvent<any>) => void>>;
    }
  ): () => void {
    const patchedOlMap = olMap as PatchedOlMap;

    if (patchedOlMap.__exclusiveMapEvents__) {
      const state = patchedOlMap.__exclusiveMapEvents__;
      const added = new Set<string>();
      options.block.forEach(t => {
        if (!state.blockedTypes.has(t)) {
          state.blockedTypes.add(t); added.add(t);
        }
      });
      const prevHandlers = options.handlers ? { ...options.handlers } : undefined;
      if (options.handlers) Object.assign(state.handlers, options.handlers);

      return () => {
        added.forEach(t => state.blockedTypes.delete(t));
        if (prevHandlers) for (const k of Object.keys(prevHandlers)) delete state.handlers[k];
        if (state.blockedTypes.size === 0) {
          (olMap as any).dispatchEvent = state.originalDispatch;
          delete patchedOlMap.__exclusiveMapEvents__;
        }
      };
    }

    const originalDispatch = olMap.dispatchEvent.bind(olMap) as DispatchFn;
    const state: State = {
      originalDispatch,
      blockedTypes: new Set(options.block),
      handlers: options.handlers ?? {},
    };

    (olMap as any).dispatchEvent = (evt: any) => {
      const type = typeof evt === 'string' ? evt : evt?.type;
      if (type && state.blockedTypes.has(type)) {
        const fn = state.handlers[type];
        if (fn && typeof evt !== 'string') fn(evt as olMapBrowserEvent<any>);
        return true;
      }
      return originalDispatch(evt);
    };

    patchedOlMap.__exclusiveMapEvents__ = state;

    return () => {
      (olMap as any).dispatchEvent = originalDispatch;
      delete patchedOlMap.__exclusiveMapEvents__;
    };
  }
}